import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButton from '@mui/material/IconButton'
import AppsIcon from '@mui/icons-material/Apps'
import { Box, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { askForAccess, logoutUser } from '../../store/actions'
import CompanyLogo from '../../assets/company/Logo.png'
import { ReactComponent as FSLogo } from '../../assets/logos/FAIR_solution.svg'
import { ReactComponent as PTLogo } from '../../assets/logos/Pay_transparency.svg'
import { ReactComponent as FSLogoDark } from '../../assets/Logo_FAIR_dark.svg'
import { ReactComponent as PTLogoDark } from '../../assets/logos/PT_dark.svg'
import { ReactComponent as Suite } from '../../assets/logos/Suite.svg'
import { ReactComponent as SuiteDark } from '../../assets/logos/Suite_dark.svg'

const FsAppsMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { token, company, account, currentTheme } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()

  const handleLogout = () => {
    dispatch(logoutUser())
    navigate('/login')
  }

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const navigateToHome = () => {
    if (token) navigate('/ines/home')
    else handleLogout()
  }

  const navigateToFs = () => {
    if (token && account?.profile.appAccess?.includes('fs')) navigate('/fs/dashboard')
    else {
      dispatch(askForAccess('fs'))
    }
  }

  const navigateToPt = () => {
    if (token && account?.profile.appAccess?.includes('pt')) navigate('/pt/home')
    else {
      dispatch(askForAccess('pt'))
    }
  }

  return (
    <div>
      <IconButton
        size="small"
        aria-label="apps of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        sx={{ color: theme.palette.text.secondary, ml: -1, mr: 1.5 }}
        onClick={handleMenu}
      >
        <AppsIcon fontSize="large" />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        sx={{
          '& .MuiMenu-paper': {
            zIndex: 1600,
            minWidth: 300,
            pt: 1,
            borderRadius: 2,
            top: '76px !important',
            left: '19px !important',
          },
        }}
      >
        <Stack alignContent="center">
          <Box
            sx={{
              backgroundColor: theme.palette.action.hover,
              borderRadius: '50%',
              padding: 1.5,
              display: 'flex',
              mx: 15,
            }}
          >
            <img
              alt="company_logo"
              src={CompanyLogo}
              style={{ height: '40px', width: '40px', objectFit: 'contain', margin: 'auto' }}
            />
          </Box>
          <Typography variant='h3' px={2} mt={1} textAlign="center">
            {company}
          </Typography>
          <Typography mt={2} variant="body2" className='ml-5 mb-1 font-semibold'>
            {t('menu.your')} APPS
          </Typography>
          <Stack spacing={2} p={2}>
            <MenuItem
              onClick={navigateToHome}
              sx={{
                height: 75,
                p: 2,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: theme.palette.action.hover,
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                  transform: 'scale(1.02)',
                },
              }}
            >
              {currentTheme === 'dark' ? (
                <SuiteDark height={35} width={115} />
              ) : (
                <Suite height={35} width={115} />
              )}
            </MenuItem>
            <MenuItem
              onClick={navigateToPt}
              sx={{
                height: 75,
                p: 2,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: theme.palette.action.hover,
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                  transform: 'scale(1.02)',
                },
              }}
            >
              {currentTheme === 'dark' ? (
                <PTLogoDark height={50} width={235} />
              ) : (
                <PTLogo height={50} width={235} />
              )}

              {!account?.profile.appAccess?.includes('pt') && (
                <Box
                  ml={2}
                  p={1}
                  py={0.25}
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="semi-bold">
                    {t('home.try')}
                  </Typography>
                </Box>
              )}
            </MenuItem>
            <MenuItem
              onClick={navigateToFs}
              sx={{
                height: 75,
                p: 2,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: theme.palette.action.hover,
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                  transform: 'scale(1.02)',
                },
              }}
            >
              {currentTheme === 'dark' ? (
                <FSLogoDark height={42} width={190} />
              ) : (
                <FSLogo height={42} width={190} />
              )}

              {!account?.profile.appAccess?.includes('fs') && (
                <Box
                  ml={2}
                  p={1}
                  py={0.25}
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="semi-bold">
                    {t('home.try')}
                  </Typography>
                </Box>
              )}
            </MenuItem>
          </Stack>
        </Stack>
      </Menu>
    </div>
  )
}

export default FsAppsMenu
